import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldAlert, ChevronDown, ChevronUp, Wand2, Info } from "lucide-react";

const formatCOP = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const MOTIVO = {
  costo_supera_precio: "Costo ≥ precio de venta",
  margen_menor_10: "Margen bruto < 10%",
  desviado_vs_actual: "Costo congelado difiere > 30% del costo real actual",
  sin_costo: "Ítem sin costo",
};

/** Finanzas → Auditoría de costos: ítems cuyo costo usado en el COGS es sospechoso. */
export default function AuditoriaCostosPanel({ desde, hasta, modo, onRecalculado }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      params.set("modo", modo || "causacion");
      const { data } = await api.get(`/reportes/auditoria-costos?${params}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo auditar"); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, desde, hasta, modo]);

  const recalcular = async () => {
    const corregibles = (data?.sospechosos || []).filter((s) => s.corregible).length;
    if (!window.confirm(`Se corregirán ${corregibles} ítem(s) sospechoso(s): los de costo congelado y los despachados SIN lote (cuando el lote ya figuraba agotado) pasan al costo real actual del producto. El COGS del periodo bajaría ≈ ${formatCOP(data?.ahorro_estimado)}. Los ítems con lote FIFO no se tocan. ¿Continuar?`)) return;
    setSaving(true);
    try {
      const { data: r } = await api.post(`/reportes/auditoria-costos/recalcular-snapshots`, { desde, hasta, modo, solo_sospechosos: true });
      toast.success(`${r.items_modificados} ítem(s) · ${r.movimientos_sin_lote_modificados || 0} despacho(s) sin lote · COGS reducido en ${formatCOP(r.cogs_reducido_en)}`);
      await load();
      onRecalculado?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo recalcular"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="auditoria-costos-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="auditoria-costos-toggle">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-600" />
          <div>
            <div className="text-sm font-semibold text-foreground">Auditoría de costos · ¿el costo de mercancía está inflado?</div>
            <div className="text-xs text-muted-foreground">Revisa ítem por ítem qué costo se usó (lote FIFO o costo congelado al vender) y detecta costos que superan el precio o difieren del costo real actual.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {loading && !data && <div className="text-sm text-muted-foreground">Auditando {desde || "…"} → {hasta || "hoy"}…</div>}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Kpi label="Ítems revisados" value={data.items} sub={`${data.items_fifo} con lote FIFO · ${data.items_snapshot} congelados · ${data.items_sin_lote || 0} despachados sin lote`} />
                <Kpi label="Sospechosos" value={data.sospechosos_total} sub={Object.entries(data.por_motivo || {}).map(([k, v]) => `${v} ${MOTIVO[k] || k}`).join(" · ") || "Ninguno"} warn={data.sospechosos_total > 0} testid="auditoria-sospechosos" />
                <Kpi label="COGS actual" value={formatCOP(data.cogs_actual)} />
                <Kpi label="COGS si recalculas congelados" value={formatCOP(data.cogs_si_recalcula_snapshots)} sub={data.ahorro_estimado ? `${data.ahorro_estimado > 0 ? "−" : "+"}${formatCOP(Math.abs(data.ahorro_estimado))}` : "Sin cambio"} accent testid="auditoria-cogs-recalc" />
              </div>
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <b>Lote FIFO</b>: el pedido ya se despachó y su costo es el real del lote consumido (no se toca). <b>Congelado</b>: pedidos confirmados sin despachar; usan el costo que tenía la ficha al crear el pedido. <b>Sin lote</b>: se despachó cuando el lote ya figuraba agotado (cargado por paquete) y tomó el costo de la ficha de ese día — si estaba inflado, aquí se arrastra. "Recalcular" reemplaza congelados y sin lote por el costo real actual.
                </div>
              </div>
              {data.sospechosos_total > 0 && (
                <div className="max-h-80 overflow-auto rounded-md border border-border">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Pedido</th><th className="text-left p-2">Producto</th><th className="text-right p-2">Cant.</th><th className="text-right p-2">Precio</th><th className="text-right p-2">Costo usado</th><th className="text-left p-2">Fuente</th><th className="text-right p-2">Costo real hoy</th><th className="text-right p-2">Impacto COGS</th><th className="text-left p-2">Motivo</th></tr></thead>
                    <tbody>
                      {data.sospechosos.map((s, i) => (
                        <tr key={`${s.pedido_id}-${s.producto_id}-${i}`} className="border-t border-border/60 tabular-nums" data-testid={`auditoria-fila-${i}`}>
                          <td className="p-2 whitespace-nowrap">#{s.numero} <span className="text-muted-foreground">{s.fecha?.slice(0, 10)}</span></td>
                          <td className="p-2 truncate max-w-[220px]">{s.producto}{s.codigo && <span className="text-muted-foreground"> · {s.codigo}</span>}</td>
                          <td className="p-2 text-right">{s.cantidad}</td>
                          <td className="p-2 text-right">{formatCOP(s.precio_unitario)}</td>
                          <td className={`p-2 text-right font-semibold ${s.costo_usado >= s.precio_unitario ? "text-rose-600" : ""}`}>{formatCOP(s.costo_usado)}</td>
                          <td className="p-2"><span className={`px-1.5 py-0.5 rounded ${s.fuente === "fifo" ? "bg-emerald-100 text-emerald-800" : s.fuente === "snapshot" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{s.fuente === "fifo" ? "Lote FIFO" : s.fuente === "snapshot" ? "Congelado" : "Sin lote"}</span></td>
                          <td className="p-2 text-right">{formatCOP(s.costo_actual)}</td>
                          <td className={`p-2 text-right ${s.impacto_cogs > 0 ? "text-rose-600" : "text-muted-foreground"}`}>{s.impacto_cogs ? formatCOP(s.impacto_cogs) : "—"}</td>
                          <td className="p-2 text-muted-foreground">{s.motivos.map((m) => MOTIVO[m] || m).join(" · ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[11px] text-muted-foreground">Los ítems "Lote FIFO" con costo alto no se corrigen aquí: revisa el lote (Recostear contenedor o Desglosar empaque en la ficha del producto).</div>
                <Button onClick={recalcular} disabled={saving || !(data.sospechosos || []).some((s) => s.corregible)} className="h-9" data-testid="auditoria-recalcular-btn">
                  <Wand2 className="w-4 h-4 mr-2" /> {saving ? "Recalculando..." : "Recalcular costos congelados sospechosos"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, warn, accent, testid }) {
  return (
    <div className={`rounded-lg border p-2.5 ${warn ? "border-rose-300 bg-rose-50/60 dark:bg-rose-900/15" : accent ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20"}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
